// Auto maintenance-mode detection.
//
// When an API call fails with a network error or a 5xx, we ping /health to
// CONFIRM the backend is really down before showing the maintenance screen —
// that way a single flaky endpoint (one 500) doesn't take the whole UI down.
// Once "down", we poll /health every 10s and auto-recover when it responds.
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || process.env.REACT_APP_API_URL;
export const HEALTH_URL = `${BACKEND_URL}/health`;
export const API_HOST = (() => { try { return new URL(BACKEND_URL).host; } catch { return BACKEND_URL; } })();

let _down = false;
let _checking = false;
let _pollTimer = null;
const _subs = new Set();

export const isDown = () => _down;

export function subscribe(fn) {
  _subs.add(fn);
  return () => _subs.delete(fn);
}

function _emit() {
  _subs.forEach((fn) => { try { fn(_down); } catch { /* ignore */ } });
}

// Raw axios (not our api instance) so the health ping never loops back through
// the interceptors that call into this module.
export async function pingHealth(timeout = 6000) {
  try {
    const res = await axios.get(HEALTH_URL, { timeout });
    return res.status === 200;
  } catch {
    return false;
  }
}

function _startPolling() {
  if (_pollTimer) return;
  _pollTimer = setInterval(async () => {
    if (await pingHealth()) {
      clearInterval(_pollTimer);
      _pollTimer = null;
      _down = false;
      _emit();
    }
  }, 10000);
}

// Called by the api interceptor on a network error or 5xx.
// A single blip (e.g. a briefly-busy worker returning one 502, or one slow
// /health) must NOT flip the whole app into maintenance — we only declare an
// outage after several spaced /health pings ALL fail.
export async function reportPossibleOutage() {
  if (_down || _checking) return;
  _checking = true;
  let ok = false;
  for (let i = 0; i < 3; i++) {
    if (await pingHealth()) { ok = true; break; }
    if (i < 2) await new Promise((r) => setTimeout(r, 1500));
  }
  _checking = false;
  if (!ok) {
    _down = true;
    _emit();
    _startPolling();
  }
}

// Any successful API response means the backend is up — clear a stale "down".
export function reportHealthy() {
  if (!_down) return;
  _down = false;
  _emit();
  if (_pollTimer) { clearInterval(_pollTimer); _pollTimer = null; }
}
